import { Authenticated, Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { authProvider, accessControlProvider, dataProvider } from "./providers.js";
import { AcceptInvitationPage, AuthPage, VerifyEmailPage } from "./pages/AuthPage.js";
import { AgentChatPage } from "./pages/AgentChat.js";
import {
  ContractorLayout,
  ContractorProjectDetail,
  ResourceListPage,
} from "./pages/ContractorCrm.js";
import {
  ConfigurationImportPage,
  ContractorSettingsPage,
  ContractorTeamPage,
} from "./pages/ContractorAdminPages.js";
import { ContractorDashboard, ContractorEndpointPage } from "./pages/ContractorDashboardPages.js";
import { ContractorLookupPage } from "./pages/ContractorLookup.js";
import { ContractorLandingPage, LandingPage } from "./pages/Landing.js";
import { CustomerContractorsPage, CustomerProfilePage } from "./pages/CustomerAccount.js";
import { ProjectDetailPage, ProjectsPage } from "./pages/Projects.js";
import { QuotePage } from "./pages/QuotePage.js";
import { ContractorDocsPage } from "./pages/ContractorDocs.js";

export function App() {
  return (
    <Refine
      routerProvider={routerProvider}
      dataProvider={dataProvider}
      authProvider={authProvider}
      accessControlProvider={accessControlProvider}
      resources={[
        { name: "projects", list: "/contractor/projects", show: "/contractor/projects/:id" },
        { name: "customers", list: "/contractor/customers" },
        { name: "runbooks", list: "/contractor/runbooks" },
        { name: "material-rules", list: "/contractor/material-rules" },
        { name: "contractor-settings", list: "/contractor/settings" },
      ]}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
        disableRouteChangeHandler: true,
      }}
    >
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/contractors" element={<ContractorLookupPage />} />
        <Route path="/chat" element={<AgentChatPage />} />
        <Route path="/chat/:contractorSlug" element={<AgentChatPage />} />
        <Route path="/c/:contractorSlug" element={<ContractorLandingPage />} />
        <Route path="/quote/:contractorSlug" element={<QuotePage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/register" element={<AuthPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route
          element={
            <Authenticated
              key="customer-routes"
              fallback={<Navigate to="/login?returnTo=/projects" replace />}
            >
              <Outlet />
            </Authenticated>
          }
        >
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/contractors" element={<CustomerContractorsPage />} />
          <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="/profile" element={<CustomerProfilePage />} />
        </Route>
        <Route path="/contractor" element={<Navigate to="/contractor/dashboard" replace />} />
        <Route
          element={
            <Authenticated
              key="contractor-routes"
              fallback={<Navigate to="/login?returnTo=/contractor/projects" replace />}
            >
              <Outlet />
            </Authenticated>
          }
        >
          <Route path="/contractor" element={<ContractorLayout />}>
            <Route path="dashboard" element={<ContractorDashboard />} />
            <Route path="leads" element={<ContractorEndpointPage endpoint="leads" />} />
            <Route path="leads/:leadId" element={<ContractorProjectDetail />} />
            <Route path="projects" element={<ResourceListPage resource="projects" />} />
            <Route path="projects/:projectId" element={<ContractorProjectDetail />} />
            <Route path="customers" element={<ResourceListPage resource="customers" />} />
            <Route path="quotes" element={<ContractorEndpointPage endpoint="quotes" />} />
            <Route path="schedule" element={<ContractorEndpointPage endpoint="schedule" />} />
            <Route path="files" element={<ContractorEndpointPage endpoint="files" />} />
            <Route path="runbooks" element={<ConfigurationImportPage />} />
            <Route path="pricing" element={<Navigate to="/contractor/runbooks" replace />} />
            <Route path="pricing-books" element={<Navigate to="/contractor/runbooks" replace />} />
            <Route
              path="project-playbooks"
              element={<Navigate to="/contractor/runbooks" replace />}
            />
            <Route path="material-rules" element={<ResourceListPage resource="material-rules" />} />
            <Route path="team" element={<ContractorTeamPage />} />
            <Route path="settings" element={<ContractorSettingsPage />} />
            <Route path="docs" element={<ContractorDocsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Refine>
  );
}
